import { CITY } from '@city'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { StationBreadcrumb } from './StationBreadcrumb'
import { SearchGlyph } from './StationPageGlyphs'
import { storeCtaHref } from '../storeCtaHref'
import { StoreLinks } from '../../../scripts/storeLinks.mjs'
import {
  reportStoreOutboundTapped,
  storeKindForUserAgent,
} from '../../analytics/storeOutboundEvents'
import { realMetroAnalyticsReporter } from '../../analytics/webAnalyticsReporter'
import type { MetroAnalyticsReporter } from '../../interaction/metroAnalyticsReporter'
import appIconLight from './assets/app-icon-light.svg'
import styles from './GlobalHeader.module.css'

interface Props {
  /** URL locale segment, e.g. `en`. */
  locale: string
  /** Display name of the current station for the breadcrumb row. */
  stationName: string
  /** Search affordance (Figma 8597:44799); wiring lands with the search section. */
  onSearchClick?: () => void
  /** Analytics sink. Optional — defaults to {@link realMetroAnalyticsReporter}. */
  reporter?: MetroAnalyticsReporter
}

/**
 * Site header of the station-page template — Figma 8597:44779
 * (TASK-699, design TASK-584): logo, primary navigation, search,
 * "Get the App" CTA, breadcrumb row. Copy resolves through the
 * `station-page` namespace; the wordmark is brand and stays English.
 *
 * `/stations`, `/lines`, `/status` index targets have no SPA view yet;
 * see the note on {@link StationBreadcrumb} before reusing this header
 * in prerendered HTML.
 */
export function GlobalHeader({
  locale,
  stationName,
  onSearchClick,
  reporter = realMetroAnalyticsReporter,
}: Props): React.JSX.Element {
  const { t } = useTranslation('station-page')
  // The visitor's store; on that store's phone the href is the city's
  // `/go/site-station-page` tracking link (TASK-1170, see storeCtaHref).
  const store = storeKindForUserAgent(navigator.userAgent)
  const storeHref = storeCtaHref(navigator.userAgent, store, StoreLinks.SLOTS.STATION_PAGE)
  // Only surfaces that EXIST — Stations / Lines / Service status index
  // pages haven't landed, and a dead link would 302 to the map
  // (crawl-budget lesson TASK-592). Re-add each as its page ships; the
  // Figma nav (8597:44779) shows the eventual full set.
  const navItems = [{ label: t('header_nav_interactive_map'), to: `/${locale}` }]
  return (
    <header className={styles.header}>
      {/* Full-width strip so the separator under the top row spans the
          viewport while the row itself stays in the 1475 container. */}
      <div className={styles.topRowStrip}>
        <div className={styles.topRow}>
          <Link to={`/${locale}`} className={styles.logo}>
            <img src={appIconLight} alt="" className={styles.appIcon} />
            <span className={styles.wordmark}>{CITY.wordmark}</span>
          </Link>
          <nav aria-label={t('header_nav_aria')} className={styles.nav}>
            {navItems.map((item) => (
              <Link key={item.label} to={item.to} className={styles.navLink}>
                {item.label}
              </Link>
            ))}
          </nav>
          <div className={styles.actions}>
            <button
              type="button"
              aria-label={t('header_search_aria')}
              className={styles.searchButton}
              onClick={onSearchClick}
            >
              <SearchGlyph size={26} />
            </button>
            <a
              href={storeHref}
              rel="nofollow"
              className={styles.getAppCta}
              onClick={() =>
                reportStoreOutboundTapped(reporter, {
                  store,
                  slot: StoreLinks.SLOTS.STATION_PAGE,
                })
              }
            >
              {t('header_get_app')}
            </a>
          </div>
        </div>
      </div>
      <StationBreadcrumb locale={locale} stationName={stationName} />
    </header>
  )
}
